import { take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';
import { IProduct } from '@cemex-core/cmx-api/models/product/models/product.interface';
import { ConfigurableFormComponent } from '../../../../../shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '../../../../../shared/modules/configurable-form/models/on-field-changed';
import { OrderBuilderState } from '../../../../reducers';
import { SpecFieldNames } from '../../constants/specification-field-names.enum';
import { unitConversion } from '../../helpers/unit-conversion.helper';
import { getAvailableContractsBalance } from '../../selectors/contract.selector';
import { getProductUnitsWithDefaults } from '../../selectors/units.selector';
import { ContractBalance } from '../../models/contract-balance';
import { IDocumentItem } from '@cemex-core/cmx-api/models/sales-document-management';
import { Unit } from '@cemex-core/cmx-api/models/common/unit';
import { RemainingBalanceValidator } from '../validators/remaining-balance.validator';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

const getContractsBalanceAndProductUnits = createSelector(
  getAvailableContractsBalance,
  getProductUnitsWithDefaults,
  (contractsBalance, productUnitsWithDefaults) => {
    return { contractsBalance, productUnitsWithDefaults };
  },
);

const getProductIdSafe = (product: IProduct) => {
  const selectedProductId = _safe(() => product.product.productId, product.productId);
  return selectedProductId;
};

@Injectable()
export class HandleRemainingBalanceCommand implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent) {
    const quantityCtrl = formGroup.get(SpecFieldNames.QUANTITY);
    const contractCtrl = formGroup.get(SpecFieldNames.CONTRACT_SELECT);
    const invoiceCtrl = formGroup.get(SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE);
    const unitCtrl = formGroup.get(SpecFieldNames.UNIT);
    const productCtrl = formGroup.get(SpecFieldNames.PRODUCT_SELECTION);

    // Validate contract balance only if theres a contract selected
    if ((contractCtrl && contractCtrl.value) || (invoiceCtrl && invoiceCtrl.value)) {
      this.store
        .select(getContractsBalanceAndProductUnits)
        .pipe(take(1))
        .subscribe(({ contractsBalance, productUnitsWithDefaults }) => {
          // Retrieve remaining balance from contract or anticipated invoice
          const balance = contractCtrl.value
            ? contractsBalance[contractCtrl.value]
            : this.invoiceContractBalance(invoiceCtrl.value);
          const selectedProductId = getProductIdSafe(productCtrl.value);
          const units = productUnitsWithDefaults.units.alternativeUnits[selectedProductId];
          const selectedUnit = units && units.find((unit) => unitCtrl.value === unit.alternativeUnit.unitId);
          const validators = [...form.config.find((config) => config.name === SpecFieldNames.QUANTITY).validation];
          quantityCtrl.clearValidators();
          if (balance && selectedUnit && balance.unitOfMeasure.unitId === selectedUnit.baseUnit.unitId) {
            const conversion = unitConversion(selectedUnit, balance.amount);
            if (conversion < quantityCtrl.value) {
              quantityCtrl.setErrors({ errorMessage: 'views.specification.exceeds_contract_balance' });
              quantityCtrl.clearValidators();
            }
            validators.push(RemainingBalanceValidator.validate(conversion));
          }
          quantityCtrl.setValidators(validators);
        });
    } else {
      quantityCtrl.clearValidators();
      quantityCtrl.setValidators(form.config.find((config) => config.name === SpecFieldNames.QUANTITY).validation);
    }
  }

  /**
   * Returns ContractBalance item from anticipated invoice
   * @param invoice
   */
  private invoiceContractBalance(invoice: IDocumentItem): ContractBalance {
    return {
      amount: Number(invoice.openedQuantity),
      unitOfMeasure: {
        unitDesc: invoice.unitOfMeasure.unitDesc,
        unitId: Number(invoice.unitOfMeasure.unitId),
        unitCode: invoice.unitOfMeasure.unitCode,
      } as Unit,
    };
  }
}
